import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Star, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LeaveReview() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [formData, setFormData] = useState({
    client_name: '',
    location: '',
    content: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_name || !formData.location || !formData.content) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/testimonials/submit`, {
        ...formData,
        rating,
      });
      setIsSuccess(true);
      toast.success('Thank you! Your review has been submitted for approval.');
    } catch (error) {
      console.error('Review submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center" data-testid="review-success-message">
        <Card className="max-w-2xl w-full mx-4">
          <CardContent className="pt-12 pb-12 text-center">
            <div className="flex justify-center mb-6">
              <CheckCircle2 className="h-20 w-20 text-green-500" />
            </div>
            <h2 className="text-3xl font-bold text-[#1e3a8a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Thank You!
            </h2>
            <p className="text-lg text-slate-600 mb-4">
              Your review has been submitted and is pending approval.
            </p>
            <p className="text-slate-500 mb-8">
              Once approved by our team, it will appear on our website.
            </p>
            <Button
              data-testid="review-back-home-btn"
              onClick={() => window.location.href = '/'}
              className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
            >
              Back to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-br from-slate-50 to-blue-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Card data-testid="review-form">
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-[#1e3a8a]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Leave a Review
            </CardTitle>
            <CardDescription className="text-base">
              Share your experience working with Raj Manda. Your feedback helps others make informed decisions.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="client_name">Your Name *</Label>
                <Input
                  id="client_name"
                  data-testid="review-name-input"
                  type="text"
                  placeholder="John Doe"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  required
                />
              </div>

              {/* Location */}
              <div className="space-y-2">
                <Label htmlFor="location">Location *</Label>
                <Input
                  id="location"
                  data-testid="review-location-input"
                  type="text"
                  placeholder="San Francisco, CA"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                />
              </div>

              {/* Rating */}
              <div className="space-y-2">
                <Label>Rating *</Label>
                <div className="flex space-x-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      data-testid={`review-star-${star}`}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                      className="transition-transform hover:scale-110"
                    >
                      <Star
                        className={`h-8 w-8 ${
                          star <= (hoveredRating || rating)
                            ? 'fill-[#d4af37] text-[#d4af37]'
                            : 'text-slate-300'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Review Content */}
              <div className="space-y-2">
                <Label htmlFor="content">Your Review *</Label>
                <Textarea
                  id="content"
                  data-testid="review-content-textarea"
                  placeholder="Tell us about your experience working with Raj Manda..."
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={6}
                  required
                />
              </div>

              {/* Submit Button */}
              <Button
                data-testid="review-submit-btn"
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white py-6 text-lg"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
